import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, CheckboxButton, Input, Title, ShortArrow } from '../shared';
import { updateAccountSetting } from '../../store/auth/auth.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectAccount } from '../../store/auth/auth.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';

const COMMON_COLUMNS = ['Season', 'Amount', 'Size.Min', 'Size.Max', 'Size.Avg'];

type ColumnKey =
  | 'seeding_report_columns'
  | 'harvest_report_columns'
  | 'assessment_report_columns'
  | 'spat_storage_report_columns'
  | 'custom_report_columns';

export const SEEDING_COLUMNS = [
  'Season',
  'Spat storage',
  'Amount',
  'Seed type',
  'Spat size',
  'Seeded date',
  'Length',
  'Catch spat',
];

export const HARVEST_COLUMNS = [
  'Season',
  'Containers',
  'Amount',
  'Size',
  'Complete date',
  'Company',
  'Vessel',
  'Bags count',
  'Harvest number',
  'Growing area',
  'Is full',
  'Is final',
  'Harvester name',
];

export const ASSESSMENT_COLUMNS = [
  'Assessed by',
  'Initial Size',
  'Size.Min',
  'Size.Max',
  'Size.Avg',
  'Assessment date',
  'Ready to harvest',
];

export const CUSTOM_REPORT_COLUMNS = [
  // Seedings
  'season',
  'initial_size',
  'seeded_date',
  'seeded_containers',
  'spat_storage',
  'seed_type',
  'catch_spat',
  // Last Assessment
  'size_min',
  'size_max',
  'size_avg',
  'assessed_by',
  'assessment_date',
  'ready_to_harvest',
  // Harvests
  'amount',
  'harvested_containers',
  'final_size',
  'complete_date',
  'company',
  'vessel',
  'harvest_number',
  'bags_count',
  'growing_area',
  'is_final',
  'is_full',
];

export const CUSTOM_COLUMN_LABELS: Record<string, string> = {
  season: 'Season',
  initial_size: 'Initial size',
  seeded_date: 'Seeded date',
  seeded_containers: 'Seeded containers',
  spat_storage: 'Spat storage',
  seed_type: 'Seed type',
  catch_spat: 'Catch spat',
  size_min: 'Size.Min',
  size_max: 'Size.Max',
  size_avg: 'Size.Avg',
  assessed_by: 'Assessed by',
  assessment_date: 'Assessment date',
  ready_to_harvest: 'Ready to harvest',
  amount: 'Amount',
  harvested_containers: 'Harvested containers',
  final_size: 'Final size',
  complete_date: 'Complete date',
  company: 'Company',
  vessel: 'Vessel',
  harvest_number: 'Harvest number',
  bags_count: 'Bags count',
  growing_area: 'Growing area',
  is_final: 'Is final',
  is_full: 'Is full',
};

export const CUSTOM_COLUMN_GROUPS: {
  label: string;
  columns: string[];
  dynamicKey?: string;
}[] = [
  {
    label: 'Seedings',
    columns: [
      'season',
      'initial_size',
      'seeded_date',
      'seeded_containers',
      'spat_storage',
      'seed_type',
      'catch_spat',
    ],
    dynamicKey: 'seeding',
  },
  {
    label: 'Last Assessment',
    columns: [
      'size_min',
      'size_max',
      'size_avg',
      'assessed_by',
      'assessment_date',
      'ready_to_harvest',
    ],
    dynamicKey: 'assessment',
  },
  {
    label: 'Harvests',
    columns: [
      'amount',
      'harvested_containers',
      'final_size',
      'complete_date',
      'company',
      'vessel',
      'harvest_number',
      'bags_count',
      'growing_area',
      'is_final',
      'is_full',
    ],
    dynamicKey: 'harvest',
  },
];

export const SPAT_STORAGE_COLUMNS = [
  'Spat Code',
  'Date Seeded',
  'Stage',
  'Containers',
  'Drop Depth',
  'Spacing',
  'Density',
  'Date Assessed',
  'Size.Min',
  'Size.Max',
  'Size.Avg',
  'Estimated Harvest',
  'Amount Per',
  'Comment',
];

export interface ReportEntry {
  title: string;
  columnKey: ColumnKey;
  allColumns: string[];
  dynamicColumns?: string[];
  dynamicGroups?: Record<string, string[]>;
  noTabs?: boolean;
}

interface SortableItemProps {
  id: string;
  label?: string;
  checked: boolean;
  onToggle: () => void;
}

const SortableItem = ({ id, label, checked, onToggle }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    cursor: 'grab',
    userSelect: 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          color: '#bbb',
          fontSize: 16,
          lineHeight: 1,
          flexShrink: 0,
          paddingRight: 4,
        }}
      >
        ⠿
      </span>
      <CheckboxButton
        className='ml-0'
        label={label ?? id}
        checked={checked}
        onChange={onToggle}
      />
    </div>
  );
};

export interface ReportColumnSectionProps {
  title: string;
  columnKey: ColumnKey;
  allColumns: string[];
  dynamicColumns?: string[];
  dynamicGroups?: Record<string, string[]>;
  noTabs?: boolean;
  onBack: () => void;
  inModal?: boolean;
  onSaveSuccess?: () => void;
}

const ReportColumnSection = ({
  title,
  columnKey,
  allColumns,
  dynamicColumns = [],
  dynamicGroups = {},
  noTabs = false,
  onBack,
  inModal = false,
  onSaveSuccess,
}: ReportColumnSectionProps) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const accSetting = useSelector(selectAccount);

  const [tab, setTab] = useState(0);

  const savedColumns: string[] = accSetting?.[columnKey] ?? [];
  const initialOrder = savedColumns.length > 0 ? savedColumns : allColumns;
  const fullOrder = [
    ...initialOrder,
    ...allColumns.filter(c => !initialOrder.includes(c)),
  ];

  const [order, setOrder] = useState<string[]>(fullOrder);
  const [checked, setChecked] = useState<string[]>(
    noTabs
      ? savedColumns
      : savedColumns.length <= 0
        ? allColumns
        : savedColumns,
  );
  const [reportName, setReportName] = useState<string>(
    accSetting?.custom_report_name ?? '',
  );
  const [disabled, setDisabled] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const commonCols = order.filter(
    c => COMMON_COLUMNS.includes(c) && allColumns.includes(c),
  );
  const staticSpecialCols = order.filter(
    c => !COMMON_COLUMNS.includes(c) && !dynamicColumns.includes(c),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrder(prev => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const toggle = (col: string) => {
    setChecked(prev =>
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col],
    );
  };

  const saveClick = async () => {
    setDisabled(true);
    const allDynamic = [
      ...dynamicColumns,
      ...Object.values(dynamicGroups).flat(),
    ];
    const columnsToSave = order.filter(c => checked.includes(c));
    const staticToSave = columnsToSave.filter(c => !allDynamic.includes(c));
    const dynToSave = columnsToSave.filter(c => allDynamic.includes(c));
    const payload: Record<string, any> = {
      [columnKey]: [...staticToSave, ...dynToSave],
    };
    if (columnKey === 'custom_report_columns') {
      payload.custom_report_name = reportName || null;
    }
    const res = await dispatch(updateAccountSetting(payload));
    if (res !== true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res?.data?.message ?? 'Server error'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Updated successfully'),
        }),
      );
      onSaveSuccess?.();
    }
    setDisabled(false);
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 18px',
    border: '1px solid #d9d9d9',
    borderBottom: active ? '1px solid #fff' : '1px solid #d9d9d9',
    borderRadius: '4px 4px 0 0',
    background: active ? '#fff' : '#f5f5f5',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? '#1a1a1a' : '#888',
    marginRight: 4,
    position: 'relative',
    bottom: -1,
  });

  const inner = (
    <>
      {!inModal && (
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 0 12px 0',
            color: '#4a90e2',
            fontSize: 13,
          }}
          type='button'
        >
          <ShortArrow />
          <span>{translate(lang, 'Back')}</span>
        </button>
      )}

      <div className='mb-16'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, title)}
        </Title>
      </div>

      {columnKey === 'custom_report_columns' && (
        <Input
          label={translate(lang, 'Report name')}
          type='text'
          className='mb-16'
          value={reportName}
          onChange={e => setReportName(e.target.value)}
          placeholder=''
        />
      )}
      {!noTabs && (
        <div style={{ borderBottom: '1px solid #d9d9d9', marginBottom: 16 }}>
          <button
            style={tabBtnStyle(tab === 0)}
            onClick={() => setTab(0)}
            type='button'
          >
            {translate(lang, 'Manage Fields')}
          </button>
          <button
            style={tabBtnStyle(tab === 1)}
            onClick={() => setTab(1)}
            type='button'
          >
            {translate(lang, 'Set Default Columns')}
          </button>
        </div>
      )}
      {!noTabs && tab === 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={commonCols}
            strategy={verticalListSortingStrategy}
          >
            {commonCols.map(col => (
              <SortableItem
                key={col}
                id={col}
                checked={checked.includes(col)}
                onToggle={() => toggle(col)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {noTabs &&
        CUSTOM_COLUMN_GROUPS.map(group => {
          const groupDynamic = group.dynamicKey
            ? (dynamicGroups[group.dynamicKey] ?? [])
            : [];
          const groupCols = order.filter(
            c => group.columns.includes(c) || groupDynamic.includes(c),
          );
          if (groupCols.length === 0) return null;
          return (
            <div key={group.label} style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#555',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {translate(lang, group.label)}
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={groupCols}
                  strategy={verticalListSortingStrategy}
                >
                  {groupCols.map(col => (
                    <SortableItem
                      key={col}
                      id={col}
                      label={translate(lang, CUSTOM_COLUMN_LABELS[col] ?? col)}
                      checked={checked.includes(col)}
                      onToggle={() => toggle(col)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          );
        })}
      {!noTabs && tab === 1 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={[...staticSpecialCols, ...dynamicColumns]}
            strategy={verticalListSortingStrategy}
          >
            {dynamicColumns.length > 0 && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#555',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {translate(lang, 'Available Columns')}
              </div>
            )}
            {staticSpecialCols.map(col => (
              <SortableItem
                key={col}
                id={col}
                checked={checked.includes(col)}
                onToggle={() => toggle(col)}
              />
            ))}
            {dynamicColumns.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#555',
                    margin: '8px 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {translate(lang, '_custom_section_columns')}
                </div>
                {dynamicColumns.map(col => (
                  <SortableItem
                    key={col}
                    id={col}
                    checked={checked.includes(col)}
                    onToggle={() => toggle(col)}
                  />
                ))}
              </>
            )}
          </SortableContext>
        </DndContext>
      )}

      <div style={{ marginTop: 20 }}>
        <Button
          type='fill'
          width='small'
          size={0}
          color='blue'
          onClick={saveClick}
          disabled={disabled}
        >
          {translate(lang, 'Update')}
        </Button>
      </div>
    </>
  );

  if (inModal) return <div>{inner}</div>;

  return (
    <div className='card' style={{ maxWidth: 560, width: '100%' }}>
      <div className='card-wrapper'>{inner}</div>
    </div>
  );
};

export default ReportColumnSection;
